import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { CropArea, calculateLockedCropArea, initialCropArea } from './crop-area';

type ImageCropperProps = {
  imageElement: HTMLImageElement;
  containerElement: HTMLElement;
  aspectRatio?: number;
  onCropChange?: (cropArea: CropArea) => void;
};

type DragState = {
  isDragging: boolean;
  dragStart: { x: number; y: number };
  dragType: 'move' | 'resize' | null;
  resizeDirection: string;
  cursor: string;
};

function calculateNewCropArea(
  currentCrop: CropArea,
  dragState: DragState,
  delta: { x: number; y: number },
  imageBounds: { left: number; top: number; right: number; bottom: number },
): CropArea {
  const newArea = { ...currentCrop };
  const minDim = 32;

  // Apply movement or resizing based on the drag type
  if (dragState.dragType === 'move') {
    newArea.x += delta.x;
    newArea.y += delta.y;
  } else if (dragState.dragType === 'resize') {
    if (dragState.resizeDirection.includes('e')) {
      newArea.width += delta.x;
    }
    if (dragState.resizeDirection.includes('w')) {
      newArea.width -= delta.x;
      newArea.x += delta.x;
    }
    if (dragState.resizeDirection.includes('s')) {
      newArea.height += delta.y;
    }
    if (dragState.resizeDirection.includes('n')) {
      newArea.height -= delta.y;
      newArea.y += delta.y;
    }
  }

  // Enforce minimum dimensions
  if (newArea.width < minDim) {
    if (dragState.resizeDirection.includes('w')) {
      newArea.x -= minDim - newArea.width;
    }
    newArea.width = minDim;
  }
  if (newArea.height < minDim) {
    if (dragState.resizeDirection.includes('n')) {
      newArea.y -= minDim - newArea.height;
    }
    newArea.height = minDim;
  }

  // Constrain the crop area to the image boundaries
  if (newArea.x < imageBounds.left) {
    newArea.width -= imageBounds.left - newArea.x;
    newArea.x = imageBounds.left;
  }
  if (newArea.y < imageBounds.top) {
    newArea.height -= imageBounds.top - newArea.y;
    newArea.y = imageBounds.top;
  }
  if (newArea.x + newArea.width > imageBounds.right) {
    newArea.width = imageBounds.right - newArea.x;
  }
  if (newArea.y + newArea.height > imageBounds.bottom) {
    newArea.height = imageBounds.bottom - newArea.y;
  }

  return newArea;
}

function imageBoundsInContainer(imageElement: HTMLElement, containerElement: HTMLElement) {
  const imageRect = imageElement.getBoundingClientRect();
  const containerRect = containerElement.getBoundingClientRect();
  return {
    left: imageRect.left - containerRect.left,
    top: imageRect.top - containerRect.top,
    right: imageRect.left - containerRect.left + imageRect.width,
    bottom: imageRect.top - containerRect.top + imageRect.height,
  };
}

export function ImageCropper({
  imageElement,
  containerElement,
  aspectRatio,
  onCropChange,
}: ImageCropperProps) {
  const [cropArea, setCropArea] = useState<CropArea>(() =>
    initialCropArea(imageBoundsInContainer(imageElement, containerElement), aspectRatio),
  );

  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    dragStart: { x: 0, y: 0 },
    dragType: null,
    resizeDirection: '',
    cursor: 'move',
  });

  const cropperRef = useRef<HTMLDivElement>(null);

  const getDirection = useCallback(
    (x: number, y: number, rect: DOMRect): string => {
      const edgeSize = aspectRatio ? Math.max(16, Math.min(rect.width, rect.height) / 5) : 10;
      const isNearTop = y < edgeSize;
      const isNearBottom = y > rect.height - edgeSize;
      const isNearLeft = x < edgeSize;
      const isNearRight = x > rect.width - edgeSize;

      if (isNearTop && isNearLeft) {
        return 'nw';
      }
      if (isNearTop && isNearRight) {
        return 'ne';
      }
      if (isNearBottom && isNearLeft) {
        return 'sw';
      }
      if (isNearBottom && isNearRight) {
        return 'se';
      }
      if (aspectRatio) {
        return 'move';
      }
      if (isNearTop) {
        return 'n';
      }
      if (isNearBottom) {
        return 's';
      }
      if (isNearLeft) {
        return 'w';
      }
      if (isNearRight) {
        return 'e';
      }
      return 'move';
    },
    [aspectRatio],
  );

  const getCursorStyle = useCallback((direction: string): string => {
    const cursors: Record<string, string> = {
      nw: 'nw-resize',
      n: 'n-resize',
      ne: 'ne-resize',
      e: 'e-resize',
      se: 'se-resize',
      s: 's-resize',
      sw: 'sw-resize',
      w: 'w-resize',
      move: 'move',
    };
    return cursors[direction] || 'default';
  }, []);

  const normalizeEvent = useCallback((e: MouseEvent | TouchEvent) => {
    if ('touches' in e) {
      const touch = e.changedTouches && e.changedTouches[0];
      if (touch) {
        return { clientX: touch.clientX, clientY: touch.clientY };
      }
    }
    return { clientX: (e as MouseEvent).clientX, clientY: (e as MouseEvent).clientY };
  }, []);

  const handleMouseDown = useCallback(
    (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      if (!cropperRef.current) {
        return;
      }

      const rect = cropperRef.current.getBoundingClientRect();
      const { clientX, clientY } = normalizeEvent(e);
      const direction = getDirection(clientX - rect.left, clientY - rect.top, rect);

      setDragState((prev) => ({
        ...prev,
        isDragging: true,
        dragStart: { x: clientX, y: clientY },
        dragType: direction === 'move' ? 'move' : 'resize',
        resizeDirection: direction,
      }));
    },
    [normalizeEvent, getDirection],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!dragState.isDragging) {
        if (!cropperRef.current) {
          return;
        }
        const rect = cropperRef.current.getBoundingClientRect();
        const { clientX, clientY } = normalizeEvent(e);
        const direction = getDirection(clientX - rect.left, clientY - rect.top, rect);
        setDragState((prev) => ({ ...prev, cursor: getCursorStyle(direction) }));
        return;
      }

      const { clientX, clientY } = normalizeEvent(e);
      const deltaX = clientX - dragState.dragStart.x;
      const deltaY = clientY - dragState.dragStart.y;

      const imageBounds = imageBoundsInContainer(imageElement, containerElement);
      const delta = { x: deltaX, y: deltaY };
      const newCropArea = aspectRatio
        ? calculateLockedCropArea(
            cropArea,
            dragState.dragType === 'move' ? 'move' : dragState.resizeDirection,
            delta,
            imageBounds,
            aspectRatio,
          )
        : calculateNewCropArea(cropArea, dragState, delta, imageBounds);

      setCropArea(newCropArea);
      setDragState((prev) => ({ ...prev, dragStart: { x: clientX, y: clientY } }));
    },
    [
      dragState,
      cropArea,
      imageElement,
      containerElement,
      aspectRatio,
      normalizeEvent,
      getDirection,
      getCursorStyle,
    ],
  );

  const handleMouseUp = useCallback(() => {
    setDragState((prev) => ({ ...prev, isDragging: false, dragType: null, resizeDirection: '' }));
  }, []);

  const handleCropperMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragState.isDragging && cropperRef.current) {
        const rect = cropperRef.current.getBoundingClientRect();
        const direction = getDirection(e.clientX - rect.left, e.clientY - rect.top, rect);
        setDragState((prev) => ({ ...prev, cursor: getCursorStyle(direction) }));
      }
    },
    [dragState.isDragging, getDirection, getCursorStyle],
  );

  useEffect(() => {
    if (dragState.isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleMouseMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [dragState.isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (onCropChange) {
      onCropChange(cropArea);
    }
  }, [cropArea, onCropChange]);

  return (
    <div
      ref={cropperRef}
      class="absolute border border-black shadow-[inset_0_0_1px_1px_white,_0_0_0_10000px_rgba(0,0,0,0.25)] z-10"
      style={{
        left: `${cropArea.x}px`,
        top: `${cropArea.y}px`,
        width: `${cropArea.width}px`,
        height: `${cropArea.height}px`,
        cursor: dragState.cursor,
        boxSizing: 'content-box',
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      onMouseMove={handleCropperMouseMove}
    >
      {/* Resize Handles */}
      <div class="absolute -top-1.5 -left-1.5 w-3 h-3 border border-gray-900 bg-white cursor-nwse-resize" />
      <div class="absolute -top-1.5 -right-1.5 w-3 h-3 border border-gray-900 bg-white cursor-nesw-resize" />
      <div class="absolute -bottom-1.5 -left-1.5 w-3 h-3 border border-gray-900 bg-white cursor-nesw-resize" />
      <div class="absolute -bottom-1.5 -right-1.5 w-3 h-3 border border-gray-900 bg-white cursor-nwse-resize" />
      {!aspectRatio && (
        <>
          <div class="absolute top-1/2 -left-1.5 -translate-y-1/2 w-3 h-3 border border-gray-900 bg-white cursor-ew-resize" />
          <div class="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-3 border border-gray-900 bg-white cursor-ew-resize" />
          <div class="absolute left-1/2 -top-1.5 -translate-x-1/2 w-3 h-3 border border-gray-900 bg-white cursor-ns-resize" />
          <div class="absolute left-1/2 -bottom-1.5 -translate-x-1/2 w-3 h-3 border border-gray-900 bg-white cursor-ns-resize" />
        </>
      )}
    </div>
  );
}

export function applyCrop(
  sourceImage: HTMLImageElement,
  cropArea: CropArea,
  containerElement: HTMLElement,
  rotation: number,
  fileType = 'image/png',
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const imageRect = sourceImage.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();

    const isRotated = rotation % 180 !== 0;
    const rotatedWidth = isRotated ? sourceImage.naturalHeight : sourceImage.naturalWidth;
    const rotatedHeight = isRotated ? sourceImage.naturalWidth : sourceImage.naturalHeight;

    const rotatedCanvas = document.createElement('canvas');
    rotatedCanvas.width = rotatedWidth;
    rotatedCanvas.height = rotatedHeight;
    const rotatedCtx = rotatedCanvas.getContext('2d');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!rotatedCtx || !ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }
    rotatedCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
    rotatedCtx.rotate((rotation * Math.PI) / 180);
    rotatedCtx.drawImage(
      sourceImage,
      -sourceImage.naturalWidth / 2,
      -sourceImage.naturalHeight / 2,
    );

    const scaleX = rotatedWidth / imageRect.width;
    const scaleY = rotatedHeight / imageRect.height;
    const cropLeft = Math.max(0, (containerRect.left + cropArea.x - imageRect.left) * scaleX);
    const cropTop = Math.max(0, (containerRect.top + cropArea.y - imageRect.top) * scaleY);
    const cropWidth = Math.round(Math.min(rotatedWidth - cropLeft, cropArea.width * scaleX));
    const cropHeight = Math.round(Math.min(rotatedHeight - cropTop, cropArea.height * scaleY));
    if (cropWidth < 1 || cropHeight < 1) {
      reject(new Error('The crop area is too small.'));
      return;
    }

    canvas.width = cropWidth;
    canvas.height = cropHeight;
    ctx.drawImage(
      rotatedCanvas,
      cropLeft,
      cropTop,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight,
    );

    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob failed.'))),
      fileType,
      0.92,
    );
  });
}
